import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import { eventsApi } from '../../api/eventsApi';
import { transcriptApi } from '../../api/transcriptApi';
import {
  ArrowLeftIcon,
  PlusCircleIcon,
  MagnifyingGlassIcon,
  TagIcon,
  PencilIcon,
  DocumentChartBarIcon,
  ArrowPathIcon,
  UserGroupIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import EditEventModal from '../../components/events/EditEventModal';
import EventReportModal from '../../components/events/EventReportModal';
import EventReportDisplay from '../../components/events/EventReportDisplay';
import { formatDuration } from '../../utils/formatDuration';
import './event-detail.css';

const getField = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== '') {
      return item[key];
    }
  }
  return undefined;
};

const normalizeStatus = (status) => String(status || 'UNKNOWN').toUpperCase();

const getStatusClass = (status) => {
  switch (normalizeStatus(status)) {
    case 'ONGOING':
    case 'ACTIVE':
    case 'IN_PROGRESS':
    case 'PROCESSING':
    case 'PENDING':
      return 'live';
    case 'COMPLETED':
      return 'completed';
    case 'PUBLISHED':
      return 'published';
    case 'FAILED':
    case 'CANCELLED':
      return 'danger';
    default:
      return 'neutral';
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatRelative = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSeconds < 60) return 'just now';
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  if (diffSeconds < 86400 * 7) return `${Math.floor(diffSeconds / 86400)}d ago`;
  return formatDate(value);
};

const getSessionDate = (session) => getField(session, 'updated_at', 'updatedAt', 'created_at', 'createdAt');
const getSessionTitle = (session) => getField(session, 'title', 'original_filename', 'originalFilename') || 'Untitled session';
const getSessionFile = (session) => getField(session, 'original_filename', 'originalFilename', 'file_path', 'filePath', 'file_url', 'fileUrl');
const getSessionUser = (session) => getField(session, 'user_name', 'userName', 'user_email', 'userEmail');
const getSessionDuration = (session) => getField(session, 'duration_seconds', 'durationSeconds');
const getSessionStatus = (session) => normalizeStatus(getField(session, 'status', 'transcript_status', 'transcriptStatus'));

export default function EventDetailPage() {
  const { id } = useParams();
  const { user, loading, error, getAuth } = useAuth();
  const [event, setEvent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('date_desc');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportKey, setReportKey] = useState(0); // Key to force report reload
  const [reportStatus, setReportStatus] = useState(null);
  const [reportElapsedTime, setReportElapsedTime] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isDownloadingSpeakers, setIsDownloadingSpeakers] = useState(false);
  const [isDownloadingTranscripts, setIsDownloadingTranscripts] = useState(false);
  const [activeTab, setActiveTab] = useState('sessions');

  const statusOptions = useMemo(() => ([
    { value: 'all', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'IN_PROGRESS', label: 'In progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'FAILED', label: 'Failed' },
  ]), []);

  const loadEventData = async () => {
    try {
      // Fetch event details from our backend
      const eventData = await eventsApi.getById(id);
      setEvent(eventData);
    } catch (e) {
      console.error('Failed to load event:', e);
    }
  };

  const loadSessions = async () => {
    try {
      // Fetch sessions for this event
      const res = await transcriptApi.getSessionsByEvent(id, {
        per_page: 100,
        compact: true,
        include_total: false,
      });
      // API returns { sessions: [...], total, skip, limit }
      const list = Array.isArray(res?.sessions) ? res.sessions : (Array.isArray(res?.results) ? res.results : (Array.isArray(res) ? res : []));
      setSessions(list);
    } catch (e) {
      console.error('Failed to load sessions:', e);
      setSessions([]);
    }
  };

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoadingAll(true);
      await Promise.all([loadEventData(), loadSessions()]);
      setLoadingAll(false);
    };
    load();
  }, [id, user]);

  const handleSaveEvent = async (updateData) => {
    await eventsApi.update(id, updateData);
    await loadEventData(); // Reload event data after update
  };

  // Check report status
  const checkReportStatus = useCallback(async () => {
    if (!id) return;
    try {
      const status = await eventsApi.getReportStatus(id);
      setReportStatus(status);
      return status;
    } catch (err) {
      console.error('Error checking report status:', err);
      return null;
    }
  }, [id]);

  // Start polling for report status
  const startPolling = useCallback(() => {
    setIsPolling(true);
    
    const poll = async () => {
      const status = await checkReportStatus();
      if (status && (status.status === 'COMPLETED' || status.status === 'FAILED')) {
        setIsPolling(false);
        // Refresh report display when completed
        if (status.status === 'COMPLETED') {
          setReportKey(prev => prev + 1);
        }
        return;
      }
      // Continue polling
      setTimeout(poll, 3000);
    };
    
    poll();
  }, [checkReportStatus]);

  // Handle report generation started
  const handleReportGenerationStarted = useCallback((result) => {
    if (result === 'started') {
      // Start polling for status updates
      startPolling();
    }
  }, [startPolling]);

  // MARKO KOD
  const handleDownloadSpeakers = async () => {
    setIsDownloadingSpeakers(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://reporting-api.diplomacy.edu'}/api/reports/events/${id}/speakers/download`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to download speakers');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `speakers_${id}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Error downloading speakers:', error);
      alert(error.message || 'Failed to download speakers');
    } finally {
      setIsDownloadingSpeakers(false);
    }
  };
  
  const handleDownloadTranscripts = async () => {
    setIsDownloadingTranscripts(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://reporting-api.diplomacy.edu'}/api/reports/events/${id}/transcripts/download`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to download transcripts');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcripts_${id}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Error downloading transcripts:', error);
      alert(error.message || 'Failed to download transcripts');
    } finally {
      setIsDownloadingTranscripts(false);
    }
  };
  // MARKO KOD KRAJ
  
  // Elapsed time timer for report generation
  useEffect(() => {
    if (!reportStatus || (reportStatus.status !== 'PROCESSING' && reportStatus.status !== 'PENDING')) {
      setReportElapsedTime(null);
      return;
    }

    const startTime = reportStatus.queued_at ? new Date(reportStatus.queued_at) : 
                      reportStatus.processing_started_at ? new Date(reportStatus.processing_started_at) : 
                      new Date();

    const updateElapsed = () => {
      const now = new Date();
      const seconds = Math.floor((now - startTime) / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes === 0) {
        setReportElapsedTime(`${remainingSeconds}sec`);
      } else if (remainingSeconds === 0) {
        setReportElapsedTime(`${minutes}min`);
      } else {
        setReportElapsedTime(`${minutes}min ${remainingSeconds}sec`);
      }
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [reportStatus]);

  // Check initial report status on load
  useEffect(() => {
    if (user && id) {
      checkReportStatus().then(status => {
        if (status && (status.status === 'PROCESSING' || status.status === 'PENDING')) {
          startPolling();
        }
      });
    }
  }, [user, id, checkReportStatus, startPolling]);

  if (!user) {
    return (
      <LoginForm 
        onSSOLogin={getAuth}
        ssoLoading={loading}
        ssoError={error}
      />
    );
  }

  const eventStatus = normalizeStatus(getField(event, 'status'));
  const eventStatusClass = getStatusClass(eventStatus);
  const eventDate = getField(event, 'created_at', 'createdAt');
  const lastActivity = sessions.reduce((latest, session) => {
    const sessionDate = getSessionDate(session);
    if (!sessionDate) return latest;
    if (!latest || new Date(sessionDate) > new Date(latest)) return sessionDate;
    return latest;
  }, getField(event, 'updated_at', 'updatedAt'));
  const totalDuration = sessions.reduce((sum, session) => sum + Number(getSessionDuration(session) || 0), 0);
  const hasDuration = sessions.some(session => Number(getSessionDuration(session) || 0) > 0);
  const contributors = [...new Set(sessions.map(getSessionUser).filter(Boolean))];
  const filteredSessions = sessions
    .filter(session => {
      const term = search.trim().toLowerCase();
      if (status !== 'all' && getSessionStatus(session) !== status) return false;
      if (!term) return true;
      return [
        getSessionTitle(session),
        getSessionFile(session),
        getSessionUser(session),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    })
    .sort((a, b) => {
      if (sort === 'date_desc') return new Date(getSessionDate(b) || 0) - new Date(getSessionDate(a) || 0);
      if (sort === 'date_asc') return new Date(getSessionDate(a) || 0) - new Date(getSessionDate(b) || 0);
      if (sort === 'title_asc') return getSessionTitle(a).localeCompare(getSessionTitle(b));
      if (sort === 'title_desc') return getSessionTitle(b).localeCompare(getSessionTitle(a));
      return 0;
    });
  const activitySessions = [...sessions].sort((a, b) => new Date(getSessionDate(b) || 0) - new Date(getSessionDate(a) || 0));
  const tabs = [
    { id: 'sessions', label: 'Sessions', count: sessions.length },
    { id: 'overview', label: 'Overview' },
    { id: 'contributors', label: 'Contributors', count: contributors.length },
    { id: 'activity', label: 'Activity' },
    { id: 'report', label: 'Report' },
  ];

  return (
    <div className="event-detail-page">
      <div className="event-detail-content">
        <Link to="/events" className="event-back-link">
          <ArrowLeftIcon /> Back to events
        </Link>

        <div className="event-detail-head">
          <div className="event-detail-main">
            <div className="event-detail-kicker">
              {event?.status && (
                <span className={`event-detail-badge event-detail-badge-${eventStatusClass}`}>
                  <span className="event-detail-badge-dot" />
                  {eventStatus.replace(/_/g, ' ')}
                </span>
              )}
              {getField(event, 'organization', 'org') && (
                <span className="event-detail-org">{getField(event, 'organization', 'org')}</span>
              )}
            </div>
            <h1>{event?.title || 'Event'}</h1>
            {event?.description && <p>{event.description}</p>}
            {event?.tags?.length > 0 && (
              <div className="event-detail-tags">
                <TagIcon />
                {event.tags.slice(0, 6).map((tag, idx) => <span key={idx}>{tag}</span>)}
              </div>
            )}
          </div>

          <div className="event-detail-actions">
            <button type="button" className="event-detail-btn" onClick={() => setIsEditModalOpen(true)}>
              <PencilIcon /> Edit
            </button>
            <button type="button" className="event-detail-btn" onClick={handleDownloadSpeakers} disabled={isDownloadingSpeakers}>
              <UserGroupIcon /> {isDownloadingSpeakers ? 'Downloading...' : 'Speakers'}
            </button>
            <button type="button" className="event-detail-btn" onClick={handleDownloadTranscripts} disabled={isDownloadingTranscripts}>
              <DocumentArrowDownIcon /> {isDownloadingTranscripts ? 'Downloading...' : 'Transcripts'}
            </button>
            {isPolling || (reportStatus && (reportStatus.status === 'PROCESSING' || reportStatus.status === 'PENDING')) ? (
              <div className="event-detail-progress">
                <ArrowPathIcon />
                <div>
                  <strong>{reportStatus?.current_step || 'Generating report'}</strong>
                  <span>
                    {reportElapsedTime || 'working'}
                    {reportStatus?.progress > 0 ? ` · ${reportStatus.progress}%` : ''}
                  </span>
                </div>
              </div>
            ) : (
              <button type="button" className="event-detail-btn event-detail-btn-primary" onClick={() => setIsReportModalOpen(true)}>
                <DocumentChartBarIcon /> Generate report
              </button>
            )}
            <Link to="/upload" className="event-detail-btn event-detail-btn-primary">
              <PlusCircleIcon /> Add session
            </Link>
          </div>
        </div>

        <div className="event-detail-stats">
          <div>
            <span>Sessions</span>
            <strong>{sessions.length}</strong>
          </div>
          {hasDuration && (
            <div>
              <span>Total duration</span>
              <strong>{formatDuration(totalDuration)}</strong>
            </div>
          )}
          <div>
            <span>Contributors</span>
            <strong>{contributors.length || '—'}</strong>
          </div>
          <div>
            <span>Created</span>
            <strong>{formatDate(eventDate)}</strong>
          </div>
          <div>
            <span>Last activity</span>
            <strong>{formatRelative(lastActivity)}</strong>
          </div>
        </div>

        <div className="event-detail-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? 'active' : ''}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.count !== undefined && <span>{tab.count}</span>}
            </button>
          ))}
        </div>

        {activeTab === 'sessions' && (
          <>
            <div className="event-session-toolbar">
              <div className="event-session-search">
                <MagnifyingGlassIcon />
                <input
                  type="text"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Search sessions..."
                />
              </div>
              <select value={status} onChange={event => setStatus(event.target.value)}>
                {statusOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select value={sort} onChange={event => setSort(event.target.value)}>
                <option value="date_desc">Newest</option>
                <option value="date_asc">Oldest</option>
                <option value="title_asc">Title A-Z</option>
                <option value="title_desc">Title Z-A</option>
              </select>
            </div>

            {loadingAll ? (
              <div className="event-detail-empty">Loading sessions...</div>
            ) : filteredSessions.length === 0 ? (
              <div className="event-detail-empty">No sessions match this event.</div>
            ) : (
              <div className="event-session-table-wrap">
                <table className="event-session-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Status</th>
                      <th>User</th>
                      {hasDuration && <th>Duration</th>}
                      <th>Updated</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.map(session => {
                      const sessionId = getField(session, 'id', 'session_id', 'sessionId');
                      const duration = getSessionDuration(session);
                      const sessionStatus = getSessionStatus(session);

                      return (
                        <tr key={sessionId}>
                          <td>
                            <Link to={`/sessions/${sessionId}`} className="event-session-title">{getSessionTitle(session)}</Link>
                            {getSessionFile(session) && <div className="event-session-sub">{getSessionFile(session)}</div>}
                          </td>
                          <td>
                            <span className={`event-detail-badge event-detail-badge-${getStatusClass(sessionStatus)}`}>
                              <span className="event-detail-badge-dot" />
                              {sessionStatus.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td>{getSessionUser(session) || '—'}</td>
                          {hasDuration && (
                            <td>{duration !== undefined && Number(duration) > 0 ? formatDuration(duration) : '—'}</td>
                          )}
                          <td>{formatDateTime(getSessionDate(session))}</td>
                          <td><Link to={`/sessions/${sessionId}`} className="event-session-open">Details</Link></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'overview' && (
          <div className="event-detail-grid">
            <div className="event-detail-card">
              <h2>About this event</h2>
              <p>{event?.description || 'No description available.'}</p>
              <div className="event-detail-kv">
                <div><span>Status</span><strong>{eventStatus.replace(/_/g, ' ')}</strong></div>
                <div><span>Created</span><strong>{formatDate(eventDate)}</strong></div>
                <div><span>Last activity</span><strong>{formatDateTime(lastActivity)}</strong></div>
                {event?.url && <div><span>URL</span><a href={event.url} target="_blank" rel="noreferrer">{event.url}</a></div>}
              </div>
            </div>
            <div className="event-detail-card">
              <h2>Available metadata</h2>
              {event?.tags?.length > 0 ? (
                <div className="event-detail-chip-list">
                  {event.tags.map((tag, idx) => <span key={idx}>{tag}</span>)}
                </div>
              ) : (
                <p>No tags or extra metadata available for this event.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'contributors' && (
          contributors.length === 0 ? (
            <div className="event-detail-empty">No contributor data available.</div>
          ) : (
            <div className="event-contributors">
              {contributors.map(contributor => {
                const userSessions = sessions.filter(session => getSessionUser(session) === contributor);
                const userDuration = userSessions.reduce((sum, session) => sum + Number(getSessionDuration(session) || 0), 0);

                return (
                  <div key={contributor} className="event-contributor-card">
                    <div className="event-contributor-avatar">{contributor.slice(0, 1).toUpperCase()}</div>
                    <div>
                      <strong>{contributor}</strong>
                      <span>
                        {userSessions.length} session{userSessions.length === 1 ? '' : 's'}
                        {userDuration > 0 ? ` · ${formatDuration(userDuration)}` : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {activeTab === 'activity' && (
          activitySessions.length === 0 ? (
            <div className="event-detail-empty">No activity yet.</div>
          ) : (
            <div className="event-timeline">
              {activitySessions.map(session => (
                <div key={getField(session, 'id', 'session_id', 'sessionId')} className="event-timeline-row">
                  <div className="event-timeline-dot" />
                  <div>
                    <div className="event-timeline-head">
                      <span>{formatRelative(getSessionDate(session))}</span>
                      <strong>{getSessionUser(session) || 'Unknown user'}</strong>
                    </div>
                    <div className="event-timeline-title">{getSessionTitle(session)}</div>
                    {getSessionFile(session) && <div className="event-session-sub">{getSessionFile(session)}</div>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === 'report' && (
          <div className="event-detail-report">
            <div className="event-detail-report-head">
              <h2>Event Report</h2>
              <button type="button" onClick={() => setIsReportModalOpen(true)}>Generate new report</button>
            </div>
            {id && <EventReportDisplay key={reportKey} eventId={id} eventUrl={event?.url} onPublished={loadEventData} />}
          </div>
        )}
      </div>

      {/* Edit Event Modal */}
      <EditEventModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEvent}
        event={event}
      />

      {/* Event Report Modal */}
      <EventReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        event={event}
        onReportGenerated={handleReportGenerationStarted}
      />
    </div>
  );
}


